import React, { useState, useCallback } from 'react';
import { Drawer, Button, Space, Typography, Spin, Divider, Tag, Alert, Tooltip } from 'antd';
import { BulbOutlined, ThunderboltOutlined, ReloadOutlined } from '@ant-design/icons';
import reportService from '../../services/reportService';

const { Text, Title } = Typography;

/**
 * AIInsightsDrawer — slides in from the right.
 * Generates per-dept-group AI strategic insights on demand.
 *
 * Props:
 *   open             boolean
 *   onClose          () => void
 *   editableGroups   { id, label|name, departments[] }[]
 *   reportData       aggregates object from backend
 *   savedInsights    { [groupKey]: insightObj }  (pre-loaded from saved report)
 *   onInsightsSaved  (insights) => void          (called when user saves)
 */
const AIInsightsDrawer = ({ open, onClose, editableGroups = [], reportData = null, savedInsights = {}, onInsightsSaved, networkName, period }) => {
  const [insights, setInsights] = useState(savedInsights || {});
  const [loading, setLoading] = useState({});  // { [groupId]: true/false }
  const [loadingAll, setLoadingAll] = useState(false);
  const [networkInsight, setNetworkInsight] = useState(null);
  const [networkLoading, setNetworkLoading] = useState(false);

  const groupKey = (g) => g.key || g.id;

  const buildGroupPayload = useCallback((group) => {
    const key = groupKey(group);

    // Find this group's aggregated stats from the departmentGroups array
    const groupStats = (reportData?.departmentGroups || []).find(dg =>
      (dg.key || dg.id) === key
    );
    if (!groupStats) return null;

    // Get questions for this group from deptGroupQuestionStats (keyed by group name)
    const groupName = (group.label || group.name || '').trim().toLowerCase();
    const groupQuestionEntry = (reportData?.deptGroupQuestionStats || []).find(s =>
      String(s?.groupName || '').trim().toLowerCase() === groupName
    );
    const questions = (groupQuestionEntry?.questions || [])
      .sort((a, b) => (a.successRate || 0) - (b.successRate || 0))
      .slice(0, 10)
      .map(q => ({ q: q.text || q.questionText || '', sc: Math.round(q.successRate || 0) }));

    const knowledgeGaps = questions.filter(q => q.sc < 60).length;

    return {
      departmentName: group.label || group.name,
      completionRate: Math.round(groupStats.completionRate || 0),
      avgScore: Math.round(groupStats.averageScore || 0),
      staffCount: groupStats.totalParticipants || 0,
      knowledgeGaps,
      questions,
    };
  }, [reportData]);

  const generateForGroup = useCallback(async (group) => {
    const payload = buildGroupPayload(group);
    if (!payload) {
      return;
    }
    const key = groupKey(group);
    setLoading(prev => ({ ...prev, [key]: true }));
    const result = await reportService.getDeptAIInsights(payload);
    setLoading(prev => ({ ...prev, [key]: false }));
    if (result.success) {
      setInsights(prev => {
        const next = { ...prev, [key]: result.data };
        if (onInsightsSaved) onInsightsSaved(next);
        return next;
      });
    }
  }, [buildGroupPayload, onInsightsSaved]);

  const generateNetworkSummary = useCallback(async () => {
    const groups = editableGroups
      .map(g => {
        const p = buildGroupPayload(g);
        if (!p) return null;
        return { name: g.label || g.name, completionRate: p.completionRate, avgScore: p.avgScore, staffCount: p.staffCount, knowledgeGaps: p.knowledgeGaps };
      })
      .filter(Boolean);
    if (!groups.length) return;
    setNetworkLoading(true);
    const result = await reportService.getNetworkInsights({ networkName, period, groups });
    setNetworkLoading(false);
    if (result.success) setNetworkInsight(result.data);
  }, [editableGroups, buildGroupPayload, networkName, period]);

  const generateAll = useCallback(async () => {
    setLoadingAll(true);
    const pending = editableGroups.filter(g => buildGroupPayload(g));
    for (const group of pending) {
      const key = groupKey(group);
      setLoading(prev => ({ ...prev, [key]: true }));
      const payload = buildGroupPayload(group);
      const result = await reportService.getDeptAIInsights(payload);
      setLoading(prev => ({ ...prev, [key]: false }));
      if (result.success) {
        setInsights(prev => ({ ...prev, [key]: result.data }));
      }
    }
    setLoadingAll(false);
    setInsights(prev => {
      if (onInsightsSaved) onInsightsSaved(prev);
      return prev;
    });
  }, [editableGroups, buildGroupPayload, onInsightsSaved]);

  const hasReportData = !!(reportData?.departmentGroups?.length);
  const generatedCount = Object.keys(insights).length;

  return (
    <Drawer
      title={
        <Space>
          <BulbOutlined style={{ color: '#2E86AB' }} />
          <span>AI Insights</span>
          {generatedCount > 0 && (
            <Tag style={{ background: '#e6f4ff', color: '#1677ff', border: '1px solid #91caff', fontSize: 11 }}>
              {generatedCount} generated
            </Tag>
          )}
        </Space>
      }
      placement="right"
      width={420}
      open={open}
      onClose={onClose}
      extra={
        hasReportData && editableGroups.length > 0 && (
          <Button
            type="primary"
            size="small"
            icon={<ThunderboltOutlined />}
            loading={loadingAll}
            onClick={generateAll}
            style={{ background: '#2E86AB', borderColor: '#2E86AB' }}
          >
            Generate All
          </Button>
        )
      }
    >
      {!hasReportData ? (
        <Alert
          type="info"
          showIcon
          message="Generate a report first"
          description="AI insights are available after you generate the report preview."
          style={{ marginBottom: 16 }}
        />
      ) : (
        <>
          {/* Network summary */}
          <div className="sugg-card" style={{ background: '#f0f7ff', borderColor: '#b5d8ec' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ margin: 0, fontSize: 13, color: '#1F5F7E' }}>Network Summary</h4>
              <Button
                size="small"
                icon={networkInsight ? <ReloadOutlined /> : <ThunderboltOutlined />}
                loading={networkLoading}
                onClick={generateNetworkSummary}
                style={{ borderColor: '#2E86AB', color: '#2E86AB' }}
              >
                {networkInsight ? 'Refresh' : 'Generate'}
              </Button>
            </div>
            {networkLoading && <div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div>}
            {!networkLoading && networkInsight && (
              <div>
                {networkInsight.strategic_summary && (
                  <p style={{ fontSize: 12, color: '#333', margin: '0 0 6px', lineHeight: 1.5 }}>{networkInsight.strategic_summary}</p>
                )}
                {networkInsight.top_performing_group && (
                  <Text style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
                    <strong style={{ color: '#52c41a' }}>Top group:</strong> {networkInsight.top_performing_group}
                  </Text>
                )}
                {networkInsight.needs_attention_group && (
                  <Text style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                    <strong style={{ color: '#fa8c16' }}>Needs attention:</strong> {networkInsight.needs_attention_group}
                  </Text>
                )}
                {networkInsight.recommended_focus_areas?.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {networkInsight.recommended_focus_areas.map((a, i) => (
                      <li key={i} style={{ fontSize: 11, color: '#444', marginBottom: 2 }}>{a}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {!networkLoading && !networkInsight && (
              <Text type="secondary" style={{ fontSize: 12 }}>Click Generate for a cross-group summary.</Text>
            )}
          </div>

          <div className="ai-hint-bar">
            <span className="ai-ico"><BulbOutlined /></span>
            <span>
              Strategic analysis per department group. Click <strong>Generate</strong> on each group,
              or use <strong>Generate All</strong> above.
            </span>
          </div>

          {editableGroups.length === 0 ? (
            <Text type="secondary">No department groups configured.</Text>
          ) : (
            editableGroups.map((group) => {
              const key = groupKey(group);
              const insight = insights[key];
              const isLoading = loading[key];
              const payload = buildGroupPayload(group);

              return (
                <div key={key} className="sugg-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{group.label || group.name}</h4>
                    <Tooltip title={!payload ? 'No data available for this group' : ''}>
                      <Button
                        size="small"
                        icon={insight ? <ReloadOutlined /> : <ThunderboltOutlined />}
                        loading={isLoading}
                        disabled={!payload}
                        onClick={() => generateForGroup(group)}
                        style={!insight ? { borderColor: '#2E86AB', color: '#2E86AB' } : {}}
                      >
                        {insight ? 'Refresh' : 'Generate'}
                      </Button>
                    </Tooltip>
                  </div>

                  {payload && (
                    <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        CR: <strong>{payload.completionRate}%</strong>
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Score: <strong>{payload.avgScore}%</strong>
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Staff: <strong>{payload.staffCount}</strong>
                      </Text>
                      {payload.knowledgeGaps > 0 && (
                        <Text style={{ fontSize: 11, color: '#cf1322' }}>
                          {payload.knowledgeGaps} gap{payload.knowledgeGaps > 1 ? 's' : ''}
                        </Text>
                      )}
                    </div>
                  )}

                  {isLoading && (
                    <div style={{ padding: '12px 0', textAlign: 'center' }}>
                      <Spin size="small" />
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>Analysing...</Text>
                    </div>
                  )}

                  {!isLoading && insight && (
                    <div>
                      {insight.strategic_summary && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>
                            Summary
                          </Text>
                          <p style={{ fontSize: 12, color: '#333', marginTop: 4, marginBottom: 0, lineHeight: 1.5 }}>
                            {insight.strategic_summary}
                          </p>
                        </div>
                      )}
                      {insight.overall_trend_insight && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>
                            Trend
                          </Text>
                          <p style={{ fontSize: 12, color: '#555', marginTop: 4, marginBottom: 0, lineHeight: 1.5 }}>
                            {insight.overall_trend_insight}
                          </p>
                        </div>
                      )}
                      {insight.recommended_focus_areas?.length > 0 && (
                        <div>
                          <Text strong style={{ fontSize: 11, textTransform: 'uppercase', color: '#888', letterSpacing: 0.5 }}>
                            Focus Areas
                          </Text>
                          <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                            {insight.recommended_focus_areas.map((area, i) => (
                              <li key={i} style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>{area}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {!isLoading && !insight && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Click Generate to analyse this group.
                    </Text>
                  )}
                </div>
              );
            })
          )}
        </>
      )}
    </Drawer>
  );
};

export default AIInsightsDrawer;
